/**
 * Image URL helpers for property photos.
 *
 * Supports two image sources:
 *   1. Our backend at `/api/files/{path}` — serves resized WebP variants via
 *      `?size=thumb|medium`.
 *   2. Airbnb muscache CDN (`a*.muscache.com`) — Akamai Image Manager supports
 *      `?im_w=<width>` transforms. Originals can be multi-MB; resized versions
 *      can be 50-95% smaller.
 *
 * External URLs we don't recognise (Unsplash, S3, placeholders, legacy fixtures)
 * pass through unchanged.
 */

// Width presets for each size tier. Kept roughly aligned with our backend
// thumb/medium output so swapping sources doesn't cause layout shift. Values
// lowered Feb 2026 to reduce image bytes ~30-40% — gallery photos are rarely
// shown wider than 800px on phone/laptop.
const THUMB_WIDTH = 360;
const MEDIUM_WIDTH = 800;

const isOurFileUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('/api/files/');
};

const isMuscacheUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return /^https?:\/\/[a-z0-9.-]*muscache\.com\//i.test(url);
};

const appendParam = (url, key, value) => {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${key}=${value}`;
};

const resizeMuscache = (url, width) => {
  // Don't double-apply if im_w already present
  if (/[?&]im_w=/.test(url)) return url;
  return appendParam(url, 'im_w', width);
};

const resizeOurFile = (url, size) => {
  if (/[?&]size=/.test(url)) return url;
  return appendParam(url, 'size', size);
};

export const thumbUrl = (url) => {
  if (isOurFileUrl(url)) return resizeOurFile(url, 'thumb');
  if (isMuscacheUrl(url)) return resizeMuscache(url, THUMB_WIDTH);
  return url;
};

export const mediumUrl = (url) => {
  if (isOurFileUrl(url)) return resizeOurFile(url, 'medium');
  if (isMuscacheUrl(url)) return resizeMuscache(url, MEDIUM_WIDTH);
  return url;
};

export default thumbUrl;
